import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {ServerConstants} from '../model/ServerConstants';
import {CommonRzmService} from './common-rzm.service';
import {RestEndPoints} from '../model/RestEndPoints';
import {AuthService} from './auth.service';
import {UpdateChangeRequestCommand} from '../generate/UpdateChangeRequestCommand';
import {RevokeTopLevelDomainCommand} from '../generate/RevokeTopLevelDomainCommand';
import {ChangeRequestVO, TldChangeResultVO} from '../generate/TldChangeResultVO';
import {TransferTopLevelDomainCommand} from '../generate/TransferTopLevelDomainCommand';
import {DelegateTopLevelDomainCommand} from '../generate/DelegateTopLevelDomainCommand';
import {DirectUpdateTopLevelDomainCommand} from '../generate/DirectUpdateTopLevelDomainCommand';
import {UpdateTopLevelDomainCommand} from '../generate/UpdateTopLevelDomainCommand';
import {TopLevelDomainVO} from '../generate/TopLevelDomainVO';
import {RequestMetadata} from '../generate/RequestMetadata';
import {ChangeVO} from '../generate/ChangeVO';

@Injectable()
export class DomainService extends CommonRzmService {

  constructor(@Inject(HttpClient) http, @Inject('env') environment, @Inject(AuthService) auth) {
    super(http, environment, auth);
  }


  getDomain(name: string): Observable<TopLevelDomainVO> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN + RestEndPoints.URL_SEPARATER + name;
    return this.getResponse<TopLevelDomainVO>(url);
  }

  getDomainByID(id: string): Observable<TopLevelDomainVO> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN_BY_ID + RestEndPoints.URL_SEPARATER + id;
    return this.getResponse<TopLevelDomainVO>(url);
  }

  getDomainOpenChangeRequests(id: string): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN_OPEN_CHANGE_REQUESTS_BY_ID + RestEndPoints.URL_SEPARATER + id;
    return this.getResponse<any>(url);
  }

  isDomainModified(domain: TopLevelDomainVO): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DOMAIN_DIFF;
    return this.getPostResponse<ChangeVO>(url, domain);
  }

  splitRequest(domain: TopLevelDomainVO): Observable<ChangeRequestVO[]> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_DIFF_AND_SPLIT;
    return this.getPostResponse<ChangeRequestVO[]>(url, domain);
  }

  confirmChangeRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    if (!requestMetadata || !requestMetadata.requestType) {
      throwError('Request Type is empty');
    }

    if (requestMetadata.requestType === 'REVOKE') {
      return this.confirmRevokeRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'NEW') {
      return this.confirmNewDelegationRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'TRANSFER') {
      return this.transferDelegationRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'UPDATE') {
      return this.updateDomainRequest(domain, requestMetadata);
    } else if (requestMetadata.requestType === 'DIRECT') {
      return this.directEditDomainRequest(domain, requestMetadata);
    }
  }


  updateExistingChangeRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata) {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const updateChangeRequestCommand: UpdateChangeRequestCommand = {};
    updateChangeRequestCommand.topLevelDomainVO = domain;
    updateChangeRequestCommand.changeRequest = requestMetadata.ticketNumber;
    updateChangeRequestCommand.comment = requestMetadata.comment;
    return this.getPostResponse<TldChangeResultVO>(url, this.createCommandEnvelope(updateChangeRequestCommand, ServerConstants.UPDATE_CHANGE_REQUEST_COMMAND));
  }

  directEditDomainRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const directUpdateTopLevelDomainCommand: DirectUpdateTopLevelDomainCommand = {};
    directUpdateTopLevelDomainCommand.TopLevelDomainVO = domain;
    directUpdateTopLevelDomainCommand.requestMetadata = requestMetadata;
    directUpdateTopLevelDomainCommand.skipRegistryUrlValidation = domain.ignoreRegistrySSLError;
    directUpdateTopLevelDomainCommand.skipRdapValidation = domain.ignoreRDAPError;
    return this.getPostResponse<TldChangeResultVO>(url, this.createCommandEnvelope(directUpdateTopLevelDomainCommand, ServerConstants.DIRECT_EDIT_TLD_COMMAND));
  }


  transferDelegationRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const transferTopLevelDomainCommand: TransferTopLevelDomainCommand = {};
    transferTopLevelDomainCommand.TopLevelDomainVO = domain;
    transferTopLevelDomainCommand.requestMetadata = requestMetadata;
    transferTopLevelDomainCommand.skipRegistryUrlValidation = !!domain.ignoreRegistrySSLError;
    transferTopLevelDomainCommand.skipRdapValidation = !!domain.ignoreRDAPError;
    return this.getPostResponse<TldChangeResultVO>(url, this.createCommandEnvelope(transferTopLevelDomainCommand, ServerConstants.TRANSFER_TLD_COMMAND));
  }

  updateDomainRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const updateTopLevelDomainCommand: UpdateTopLevelDomainCommand = {};
    updateTopLevelDomainCommand.TopLevelDomainVO = domain;
    updateTopLevelDomainCommand.requestMetadata = requestMetadata;
    updateTopLevelDomainCommand.skipRegistryUrlValidation = !!domain.ignoreRegistrySSLError;
    updateTopLevelDomainCommand.skipRdapValidation = !!domain.ignoreRDAPError;
    return this.getPostResponse<TldChangeResultVO>(url, this.createCommandEnvelope(updateTopLevelDomainCommand, ServerConstants.UPDATE_TLD_COMMAND));
  }

  confirmRevokeRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const revokeTopLevelDomainCommand: RevokeTopLevelDomainCommand = {};
    revokeTopLevelDomainCommand.tld = domain.id;
    revokeTopLevelDomainCommand.requestMetadata = requestMetadata;
    return this.getPostResponse<TldChangeResultVO>(url, this.createCommandEnvelope(revokeTopLevelDomainCommand, ServerConstants.REVOKE_TLD_COMMAND));
  }


  confirmNewDelegationRequest(domain: TopLevelDomainVO, requestMetadata: RequestMetadata): Observable<TldChangeResultVO> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;

    const delegateTopLevelDomainCommand: DelegateTopLevelDomainCommand = {};
    delegateTopLevelDomainCommand.TopLevelDomainVO = domain;
    delegateTopLevelDomainCommand.requestMetadata = requestMetadata;
    return this.getPostResponse<TldChangeResultVO>(url, this.createCommandEnvelope(delegateTopLevelDomainCommand, ServerConstants.DELEGATE_TLD_COMMAND));
  }


  getListOfDomains(page: number, size: number, filter: string, searchText: string): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.GET_LIST_OF_DOMAINS + '?page=' + page + '&size=' + size + '&filter=' + filter + '&search=' + searchText;
    return this.getResponse<any>(url);
  }


  createContactsIfNull(domain: TopLevelDomainVO) {
    if (domain.organization == null) {
      domain.organization = {
        address: {}
      };
    }

    if (domain.administrative == null) {
      domain.administrative = {
        address: {}
      };
    }

    if (domain.technical == null) {
      domain.technical = {
        address: {}
      };
    }
  }

}
